import cn from 'classnames';
import { useEffect, useState } from 'react';

import { Select } from '@webapp/common/components/select';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { LocalStorage } from '@webapp/common/lib/storage';
import type { Language, SurveyLanguage } from '@webapp/common/resources/entities/language';

import { getSurveyLang, surveyApi } from '@webapp/survey/src/machine/api';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';
import { useStore } from '@webapp/survey/src/store';

import css from './lang.css';

export const SurveyLang: VFC<{ surveyId: number; defaultLang?: string }> = ({ surveyId, defaultLang }) => {
    const isMobile = useMobile();
    const ctx = useStateSelector((ctx) => ctx);
    const store = useStore();
    const [surveyLanguagesOptions, setSurveyLanguages] = useState<Array<{ label: string; value: string }>>([]);

    useEffect(() => {
        if (surveyId) {
            surveyApi.getLanguages(surveyId).then((resp) => {
                const languagesData = (resp.data || []) as Array<SurveyLanguage>;
                const langs = languagesData
                    .filter((item) => item.status === 'full')
                    .map((lang) => ({ value: lang.code, label: lang.nativeName }));

                if (defaultLang && !langs.find((item) => item.value === defaultLang)) {
                    langs.unshift({ value: defaultLang, label: defaultLang });
                }

                setSurveyLanguages(langs);

                surveyApi.getAllLanguages().then((resp) => {
                    const allLanguagesData = (resp.data || []) as Array<Language>;
                    const findDefaultLangFullObj = allLanguagesData.find((item) => item.code === defaultLang);

                    if (findDefaultLangFullObj) {
                        setSurveyLanguages((prev) => {
                            const newLangs = [...prev];
                            newLangs[0].label = findDefaultLangFullObj.nativeName;

                            return newLangs;
                        });
                    }
                });
            });
        }
    }, [defaultLang, isMobile, surveyId]);

    const onChange = (value) => {
        if (value === defaultLang) {
            LocalStorage.remove('lang');
        } else {
            LocalStorage.set('lang', value);
        }

        const search = new URLSearchParams(window.location.search);
        search.delete('lang');
        const searchString = search.toString();

        surveyApi.updateTranslationsPagesQuestions(ctx, value.toString()).then((res) => {
            store.send({ type: 'UPDATE_TRANSLATIONS_QUESTIONS_PAGES', data: res });
        });

        surveyApi.updateTranslationsInfo(ctx, value.toString()).then((res) => {
            store.send({ type: 'UPDATE_TRANSLATIONS_INFO', data: res });
        });

        if (history.pushState) {
            const newUrl =
                window.location.protocol +
                '//' +
                window.location.host +
                window.location.pathname.replace(/\/{2,}/g, '/') +
                (searchString ? `?${searchString}` : '');
            window.history.pushState({ path: newUrl }, '', newUrl);
        }
    };

    return (
        <div
            className={cn(css.lang, 'lang-selector', {
                [css.lang_hidden]: surveyLanguagesOptions.length < 2,
                [css.mobile]: isMobile,
            })}
        >
            <Select
                view='transparent'
                value={getSurveyLang() || defaultLang}
                placeholder='Язык/Language'
                options={surveyLanguagesOptions}
                stretchMenuToLongestOption={true}
                onChange={onChange}
            />
        </div>
    );
};
